import { Flex } from '@chakra-ui/react';
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@mesh.ai/rabbit';
import { LabelFieldWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/LabelFieldWrapper';

import { ChoiceQuestion, EmojiQuestion, ScaleQuestion, TextQuestion } from 'assets/assetsV1/iconComponents';

import { EngagementDriverType } from '../../types';
import { ENGAGEMENT_SCALE_TYPE_OPTIONS, MAX_OPTIONS_FOR_NPS } from '../constants/AddQuestionConstant';
import { generateEngagementScaleOptions } from '../helpers';
import {
  EngagementAnswerType,
  EngagementQuestionType,
  useAddEngagementQuestion,
} from '../slice/AddEngagementQuestion.slice';

type Props = {
  handleQuestionChange: (value: Partial<EngagementQuestionType>) => void;
  drivers: EngagementDriverType[];
};

export const ANSWER_TYPE_OPTIONS: Array<{
  value: EngagementAnswerType;
  label: string;
  Icon: React.ComponentType;
}> = [
  { value: 'EMOJI', label: 'Emoji-based', Icon: EmojiQuestion },
  { value: 'NUMBER_RATING', label: 'Scale-based', Icon: ScaleQuestion },
  { value: 'DROP_DOWN', label: 'Choice-based', Icon: ChoiceQuestion },
  { value: 'NONE', label: 'Open-ended', Icon: TextQuestion },
];

const AnswerTypeDropdown = ({ handleQuestionChange, drivers }: Props) => {
  const { answerType, maxOptions, scaleType, tag } = useAddEngagementQuestion();

  return (
    <LabelFieldWrapper label="Answer type" gridTemplateColumns="200px 1fr" gridGap={0}>
      <Select
        value={answerType}
        onValueChange={(value: EngagementAnswerType) => {
          const isChoiceBased = value === 'DROP_DOWN';
          const choiceBasedOptions = generateEngagementScaleOptions(2);
          handleQuestionChange({
            answerType: value,
            maxOptions: isChoiceBased ? 2 : 3,
            answerOptions: isChoiceBased
              ? choiceBasedOptions
              : ENGAGEMENT_SCALE_TYPE_OPTIONS[scaleType ?? 'AGREE_DISAGREE'][3],
          });

          // if it's number rating and 10, then it's NPS
          // in that case check the selected tag.uuid is of type ENPS or MNPS
          // if not, then set it to null
          if (value === 'NUMBER_RATING' && maxOptions === MAX_OPTIONS_FOR_NPS && tag?.uuid) {
            const selectedDriver = drivers?.find(({ id }) => id === tag?.uuid);
            if (['ENPS', 'MNPS'].includes(selectedDriver?.tagSubType)) {
              handleQuestionChange({ tag: null });
            }
          }

          if (value === 'NONE') {
            handleQuestionChange({
              enableFeedbackText: true,
              isTextRequired: true,
              answerOptions: [],
            });
          }
        }}
      >
        <SelectTrigger style={{ width: '100%', textAlign: 'left' }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent style={{ maxWidth: '100%' }}>
          <SelectGroup>
            {ANSWER_TYPE_OPTIONS.map(({ value, label, Icon }) => (
              <SelectItem key={value} value={value}>
                <Flex align="center" gap="8px">
                  <Icon />
                  {label}
                </Flex>
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </LabelFieldWrapper>
  );
};

export { AnswerTypeDropdown };
